import React, { Suspense, lazy } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import AdminRoute from './AdminRoute';
import AppShell from '../components/shell/AppShell';
import LoginPage from '../Login';
import MaintenancePage from '../MaintenancePage';
import HomePage from '../pages/HomePage';

/*
 * Chargement différé.
 *
 * L'application partait sur un unique paquet de 386 Ko : chaque visiteur
 * téléchargeait le panneau d'administration, l'uploader de jeux et la
 * watch-party avant même de voir la page d'accueil. Ces écrans ne sont
 * demandés que lorsqu'on y va vraiment.
 */
const AdminPanel = lazy(() => import('../AdminPanel'));
const ExplorePage = lazy(() => import('../pages/ExplorePage'));
const ActorPage = lazy(() => import('../pages/ActorPage'));
const ActorsPage = lazy(() => import('../pages/ActorsPage'));
const SeriesCalendar = lazy(() => import('../components/SeriesCalendar'));
const PublicGamesLibrary = lazy(() => import('../components/PublicGamesLibrary'));
const SetupAccount = lazy(() => import('../components/SetupAccount'));
const WatchPartyPage = lazy(() => import('../components/WatchPartyPage'));
const VotingPage = lazy(() => import('../components/votes/VotingPage'));
const AchievementsPage = lazy(() => import('../components/AchievementsPage'));
const MusiquePage = lazy(() => import('../pages/MusiquePage'));


/** Attente d'un écran différé — discrète, à la place du contenu. */
function RouteFallback() {
  return (
    <div className="shell page-bottom-space" style={{ paddingTop: 'calc(var(--h-header) + 3rem)' }}>
      <div className="skeleton" style={{ height: 220, borderRadius: 'var(--r-xl)' }} />
      <div className="grid-posters mt-6">
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="skeleton" style={{ aspectRatio: '2 / 3' }} />
        ))}
      </div>
    </div>
  );
}

export default function AppRoutes({
  isAuthenticated,
  currentUser,
  maintenanceEnabled,
  maintenanceMessage,
  onDisableMaintenance,
  allMovies,
  fetchAllMoviesForAdmin,
  onLoginSuccess,
  shell,
}) {
  // Maintenance : tout le monde sauf l'administrateur voit la page dédiée.
  if (maintenanceEnabled && currentUser?.role !== 'admin') {
    return (
      <Routes>
        <Route path="*" element={<MaintenancePage message={maintenanceMessage} />} />
      </Routes>
    );
  }

  /**
   * Gabarit des écrans connectés : l'enveloppe de navigation est montée une
   * seule fois et les pages défilent dedans via `Outlet`. Auparavant chaque
   * route recevait l'intégralité de l'accueil en `children`, ce qui remontait
   * le carrousel et les rails à chaque changement de page.
   */
  const Shell = () => (
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AppShell {...shell} currentUser={currentUser}>
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </AppShell>
    </ProtectedRoute>
  );

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <LoginPage onLoginSuccess={onLoginSuccess} />
          }
        />
        <Route path="/setup-account" element={<SetupAccount />} />

        <Route
          path="/admin/*"
          element={
            <AdminRoute isAuthenticated={isAuthenticated} currentUser={currentUser}>
              <AdminPanel
                movies={allMovies}
                onClose={() => window.history.back()}
                onMoviesUpdate={fetchAllMoviesForAdmin}
                maintenanceEnabled={maintenanceEnabled}
                onDisableMaintenance={onDisableMaintenance}
              />
            </AdminRoute>
          }
        />

        <Route element={<Shell />}>
          <Route path="/" element={<HomePage onOpenTheater={shell?.onOpenTheater} />} />
          {/* Fiche ouverte : l'accueil reste derrière pour conserver le
              défilement au moment de refermer. */}
          <Route path="/film/:id" element={<HomePage onOpenTheater={shell?.onOpenTheater} />} />
          <Route path="/serie/:tmdbId" element={<HomePage onOpenTheater={shell?.onOpenTheater} />} />

          <Route path="/explorer" element={<ExplorePage />} />
          <Route path="/acteurs" element={<ActorsPage />} />
          <Route path="/acteur/:name" element={<ActorPage />} />

          <Route path="/calendrier" element={<SeriesCalendar currentUser={currentUser} isAdmin={currentUser?.role === 'admin'} />} />
          <Route path="/games" element={<PublicGamesLibrary />} />
          <Route path="/achievements" element={<AchievementsPage currentUser={currentUser} />} />
          <Route path="/musique" element={<MusiquePage />} />
          <Route path="/vote/:pollId" element={<VotingPage currentUser={currentUser} />} />
          <Route path="/watch-party/:eventId" element={<WatchPartyPage currentUser={currentUser} />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
